/**
 * PropertyEditorMeta.js
 *
 * Derives typed-editor metadata for widget properties from v2 schemas.
 *
 * Widget schemas already describe every property (type, min/max, enum
 * values, resource kind, defaults) — this module maps that description onto
 * the editor the Automation Editor should render for a property's constant
 * value. Adding a new widget property therefore requires no editor changes:
 * the schema entry alone selects the editor.
 *
 * Editors:
 *   'color'  — color picker (with theme colors)
 *   'bool'   — true/false selector
 *   'number' — numeric editor (min/max from schema)
 *   'enum'   — dropdown of the schema's allowed values
 *   'font'   — font selector (internal + project fonts)
 *   'image'  — image resource selector
 *   'text'   — plain text (only when nothing better exists)
 *
 * This module is pure — no Vue, no Konva imports — so it is
 * safe to use from any layer (and from node-based tests).
 *
 * @typedef {{
 *   editor: string,
 *   values?: string[],
 *   min?: number,
 *   max?: number,
 *   default?: *,
 * }} PropertyEditorMeta
 */

import { availableStrategies } from '../../Hasp/properties/v2/bindings.js';
import { hasRuntimeWritableMetadata, resolveRuntimeWireName } from '../../Hasp/properties/v2/runtime.js';

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Build the editor metadata map for every property of a widget schema.
 *
 * @param {object|null} schema — v2 widget schema (node.schemaV2)
 * @param {string[]} properties — property names to describe (node.hass_properties)
 * @returns {Object<string, PropertyEditorMeta>}
 */
export function derivePropertyEditorMeta(schema, properties) {
  const out = {};
  for (const property of properties ?? []) {
    out[property] = getPropertyEditorMeta(schema, property);
  }
  return out;
}

/**
 * Editor metadata for a single property.
 *
 * @param {object|null} schema
 * @param {string} property
 * @returns {PropertyEditorMeta}
 */
export function getPropertyEditorMeta(schema, property) {
  const def  = _findPropertyDef(schema, property) ?? _gaugeNeedlePropertyDef(property);
  const meta = { editor: _editorFor(property, def) };

  if (def?.values) meta.values = [...def.values];
  if (def?.min !== undefined) meta.min = def.min;
  if (def?.max !== undefined) meta.max = def.max;

  const dflt = schema?.defaults?.[property];
  if (dflt !== undefined) meta.default = dflt;

  return meta;
}

/** Generic leaf-editor selection for an already-resolved v2 property. */
export function getEditorForPropertyDefinition(definition) {
  return _editorFor('', definition);
}

/**
 * Binding strategies available for one property — see
 * Hasp/properties/v2/bindings.js's availableStrategies(). Resolves the
 * property's schema `type` (the same lookup getPropertyEditorMeta uses
 * above) and its bindings whitelist entry, if any, then defers to that
 * pure lookup for the actual strategy-list resolution.
 *
 * Reads `bindings.properties` (the current schema key — the pre-rename
 * `bindings.state` name is gone). Only a flat top-level override is
 * checked; no current schema declares a part-scoped override.
 *
 * @param {object|null} schema
 * @param {string} property
 * @returns {string[]} strategy keys, e.g. ['constant', 'mapping', 'state']
 */
export function getAvailableStrategies(schema, property) {
  const def        = _findPropertyDef(schema, property) ?? _gaugeNeedlePropertyDef(property);
  // On a schema migrated to runtime metadata, a bindings.properties entry is
  // an override for an already-writable property — it never grants strategies
  // to a property that `runtime.writable` does not expose (see v2/runtime.js).
  const overridable = !hasRuntimeWritableMetadata(schema)
    || resolveRuntimeWireName(schema, property) !== null;
  const bindingDef = overridable ? schema?.bindings?.properties?.[property] : undefined;
  return availableStrategies(def?.type, bindingDef);
}

// ── Derivation ────────────────────────────────────────────────────────────────

/** @private — look a property up in top-level properties, then in part styles */
function _findPropertyDef(schema, property) {
  if (!schema) return null;
  if (schema.properties?.[property]) return schema.properties[property];

  for (const part of Object.values(schema.parts ?? {})) {
    if (part?.properties?.[property]) return part.properties[property];
  }
  return null;
}

/** Gauge needle leaves are dynamic collection properties, so they do not
 * have individual schema entries. They still use the normal typed binding
 * editors and strategy table. */
function _gaugeNeedlePropertyDef(property) {
  const match = /^needles\[\d+\]\.(val|color)$/.exec(String(property ?? ''));
  if (!match) return null;
  return { type: match[1] === 'color' ? 'color' : 'int' };
}

/**
 * @private
 *
 * NOTE — this switch and DEFAULT_STRATEGIES_BY_TYPE (Hasp/properties/v2/bindings.js)
 * both branch on the same property `type` vocabulary ('color', 'bool', 'int',
 * 'float', 'string', 'enum', 'resource') independently — one selects a leaf
 * editor, the other selects available binding strategies. They are not
 * derived from each other. Adding a new property `type` should update both.
 */
function _editorFor(property, def) {
  // Explicit editor override in the schema always wins
  if (def?.editor) return def.editor;

  switch (def?.type) {
    case 'color':  return 'color';
    case 'bool':   return 'bool';
    case 'int':
    case 'float':
    case 'number': return 'number';
    case 'enum':   return 'enum';
    case 'resource':
      if (def.resourceType === 'font')  return 'font';
      if (def.resourceType === 'image') return 'image';
      return 'text';
    default:
      break;
  }

  // Heuristics for binding-only names with no schema entry (val, w, h, …)
  if (property === 'src') return 'image';
  if (property.includes('color')) return 'color';
  if (/^(val|min|max|start_value|w|h|x|y|page)$/.test(property)) return 'number';
  return 'text';
}
